import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Send, CheckCircle2, Truck, Boxes, Ship } from 'lucide-react';
import { NavLink } from 'react-router-dom';

export default function Contact() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Contact Us — CROSSDOCS</title>
        <meta name="description" content="Get in touch with CROSSDOCS for logistics solutions. Request a quote, discuss your needs, or learn more about our services." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Let's Discuss Your
              <span className="block text-primary-400">Logistics Needs</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Ready to optimize your supply chain? Our team of logistics experts is here to help 
              you find the perfect solution for your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <div className="grid gap-16 lg:grid-cols-2 items-start">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Get Your Free Quote</h2>
                <p className="text-slate-600 mb-8">
                  Tell us about your freight requirements and we'll respond within 24 hours with a 
                  customized solution and competitive pricing.
                </p>
                
                <form className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
            <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                      <input 
                        type="text" 
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200" 
                        placeholder="Jane Doe" 
                      />
            </div>
            <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company *</label>
                      <input 
                        type="text" 
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200" 
                        placeholder="Acme Inc." 
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <input 
                        type="email" 
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200" 
                        placeholder="jane@acme.com" 
                      />
            </div>
          </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Service Type *</label>
                    <select 
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    >
                      <option value="">Select a service</option>
                      <option value="CROSSDOCS">CROSSDOCS</option>
                      <option value="line-haul">Line-Haul</option>
                      <option value="warehousing">Warehousing</option>
                      <option value="ocean-freight">Ocean & Drayage</option>
                      <option value="air-cargo">Air & Expedited</option>
                      <option value="last-mile">Last-Mile Delivery</option>
                      <option value="consulting">Supply Chain Consulting</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Details *</label>
                    <textarea 
                      rows="5" 
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200" 
                      placeholder="Describe your lanes, volume, timelines, and any specific requirements..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold py-4 px-8 rounded-xl hover:from-primary-700 hover:to-primary-800 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <Send className="h-5 w-5" />
                    Request Free Quote
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
            <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Get in Touch</h3>
                <p className="text-slate-600 mb-8">
                  Our logistics experts are ready to help you optimize your supply chain. 
                  Reach out via email or fill out the form for a quick response.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <Mail className="h-6 w-6 text-primary-600" />
            </div>
            <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Email Us</h4>
                    <p className="text-slate-600">sales@crossdocs.co</p>
                    <p className="text-slate-600">support@crossdocs.co</p>
                  </div>
            </div>
          </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200">
                <h4 className="text-lg font-semibold text-slate-900 mb-4">Why Choose CROSSDOCS?</h4>
                <div className="space-y-3">
                  {[
                    '24-hour response guarantee',
                    'Custom logistics solutions',
                    'Real-time tracking & visibility',
                    'Competitive pricing',
                    'Nationwide network coverage',
                    'Dedicated account managers'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary-600 flex-shrink-0" />
                      <span className="text-slate-600">{benefit}</span>
                    </div>
                  ))}
                </div>
          </div>
            </motion.div>
      </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container-consistent text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Logistics?
            </h2>
            <p className="text-lg text-primary-100 max-w-3xl mx-auto mb-8">
              Join hundreds of companies already experiencing the CROSSDOCS difference. 
              Get started today with a free consultation and quote.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/services" 
                className="inline-flex items-center rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Explore Services
              </NavLink>
              <NavLink 
                to="/about" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-base font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Learn About Us
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






